import React, {useCallback, useRef, useState} from 'react';
import {Button, Tabs, Tooltip} from 'antd';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {EditOutlined, SaveOutlined} from '@ant-design/icons';
import {useTranslation} from '../../../hooks/useTranslation';
import {useEventDetail} from '../../../hooks/events/useEvents';
import EventDetailNavigation from './navigation/EventDetailNavigation';
import {EventFormActions} from './panels/EventForm';
import PageContainer from '../../../components/common/PageContainer';
import PrimaryButton from '../../../components/common/PrimaryButton';
import BootstrapIcon from '../../../components/common/BootstrapIcon';
import '../events.less';

const EventDetail: React.FC = () => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {t} = useTranslation();

  const isEditMode = !!id && id !== 'new';
  const {data: eventData} = useEventDetail(isEditMode ? id : undefined);
  const eventCode = (eventData as any)?.externalCode || (id === 'new' ? 'new' : '—');

  const activePanel = searchParams.get('panel') || 'event-form';

  const formActionsRef = useRef<EventFormActions | null>(null);
  const [headerSaving, setHeaderSaving] = useState(false);

  const handleRegisterActions = useCallback((actions: EventFormActions) => {
    formActionsRef.current = actions;
  }, []);

  const handlePanelChange = (panelKey: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('panel', panelKey);
      return next;
    });
  };

  const handleSave = () => {
    formActionsRef.current?.submit();
  };

  const navigationOptions = [
    {
      key: 'event-form',
      icon: <EditOutlined/>,
      titleKey: 'events.detail.navigation.eventDetails.title',
      subtitleKey: 'events.detail.navigation.eventDetails.subtitle',
    },
    {
      key: 'event-financial',
      icon: <BootstrapIcon icon="currency-dollar"/>,
      titleKey: 'events.detail.navigation.financial.title',
      subtitleKey: 'events.detail.navigation.financial.subtitle',
    },
    {
      key: 'event-approval',
      icon: <BootstrapIcon icon="person-check"/>,
      titleKey: 'events.detail.navigation.approval.title',
      subtitleKey: 'events.detail.navigation.approval.subtitle',
    },
    {
      key: 'event-activity',
      icon: <BootstrapIcon icon="activity"/>,
      titleKey: 'events.detail.navigation.activity.title',
      subtitleKey: 'events.detail.navigation.activity.subtitle',
    },
  ];

  const showHeaderActions = activePanel === 'event-form';

  return (
    <PageContainer
      className="event-detail-page"
      title={`${t('events.detail.header.titlePrefix')} ${eventCode}`}
      subTitle={t('events.detail.header.subtitle')}
      bordered={false}
      extra={
        showHeaderActions ? (
          <>
            <Button onClick={() => navigate('/events')}>
              {t('events.detail.header.cancel')}
            </Button>
            <PrimaryButton
              type="primary"
              icon={<SaveOutlined/>}
              loading={headerSaving}
              onClick={handleSave}
            >
              {t('events.saveEvent')}
            </PrimaryButton>
          </>
        ) : undefined
      }
      tabs={
        <Tabs
          className="gt-event-tabs"
          activeKey={activePanel}
          onChange={handlePanelChange}
          type="line"
          size="middle"
          tabBarStyle={{margin: 0}}
          items={navigationOptions.map(option => ({
            key: option.key,
            label: (
              <Tooltip title={t(option.subtitleKey)} placement="bottom">
                <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                  {option.icon}
                  <span>{t(option.titleKey)}</span>
                </span>
              </Tooltip>
            ),
          }))}
        />
      }
    >
      <div style={{flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden'}}>
        <EventDetailNavigation
          eventId={id}
          activePanel={activePanel}
          onRegisterActions={handleRegisterActions}
          onSavingChange={setHeaderSaving}
        />
      </div>
    </PageContainer>
  );
};

export default EventDetail;
